"use client";

import { useState } from "react";
import { ChevronLeft, ChevronRight, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhispersOfElegance() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const testimonials = [{
    id: 1,
    name: "Elena Vance",
    credentials: "Lead Perfumer, L'Artisan Studio",
    quote: "The depth of Essence & Co.'s Midnight Bloom reveals new layers of complexity with every wear — it has fundamentally changed my perspective on modern floral compositions.",
    publication: "Featured in The Olfactory Review, 2024",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701222/img/testimonial_elena_vance.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701222/img/testimonial_elena_vance.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701222/img/testimonial_elena_vance.png-wvc-sizes",
    rating: 5
  }, {
    id: 2,
    name: "Julian Thorne",
    credentials: "Fragrance Critic, Scented Journal",
    quote: "A masterclass in botanical balance and sensory poetry. Essence & Co. has redefined what a luxury boutique fragrance can aspire to be in the modern era.",
    publication: "Featured in The Scented Journal, 2024",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701222/img/testimonial_julian_thorne.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701222/img/testimonial_julian_thorne.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701222/img/testimonial_julian_thorne.png-wvc-sizes",
    rating: 5
  }, {
    id: 3,
    name: "Sofia Moretti",
    credentials: "Creative Director, Atelier Essence",
    quote: "Their signature sandalwood base carries a depth of character I have not encountered elsewhere. Every application is a quiet revelation of pure elegance.",
    publication: "Featured in Tales of the Perfumer, 2024",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701222/img/testimonial_sofia_moretti.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701222/img/testimonial_sofia_moretti.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701222/img/testimonial_sofia_moretti.png-wvc-sizes",
    rating: 5
  }, {
    id: 4,
    name: "Marcus Sterling",
    credentials: "Founder, The Fragrance Collective",
    quote: "Essence & Co. distills not just scents but an entire philosophy — patience, terroir, and reverence for the botanical world in every exquisite bottle.",
    publication: "Featured in The World of Fine Fragrance, 2024",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701222/img/testimonial_marcus_sterling.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701222/img/testimonial_marcus_sterling.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701222/img/testimonial_marcus_sterling.webp-wvc-sizes",
    rating: 5
  }];
  const [activeIndex, setActiveIndex] = useState(0);
  const active = testimonials[activeIndex];
  const thumbnails = testimonials.filter((_, i) => i !== activeIndex);
  const handlePrev = () => {
    setActiveIndex(prev => prev === 0 ? testimonials.length - 1 : prev - 1);
  };
  const handleNext = () => {
    setActiveIndex(prev => prev === testimonials.length - 1 ? 0 : prev + 1);
  };
  return <section data-section-id="3182" id="whispers-of-elegance" className="bg-background text-foreground py-20 md:py-28 px-4 md:px-8 lg:px-16 overflow-hidden">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        <div className="flex items-center justify-center gap-4 mb-16">
          <div className="h-px flex-1 max-w-xs bg-border" />
          <Leaf className="w-5 h-5" style={{
          color: "var(--primary)"
        }} strokeWidth={1} />
          <div className="h-px flex-1 max-w-xs bg-border" />
        </div>

        {}
        <div className="text-center mb-16 md:mb-20">
          <p className="font-mono text-xs tracking-[0.3em] uppercase mb-4" style={{
          color: "var(--primary)"
        }}>
            05 —
          </p>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-muted-foreground mb-6">
            Whispers of Elegance
          </p>
          <h2 className="font-default italic text-4xl md:text-5xl lg:text-[56px] leading-tight text-foreground">
            In Their Words
          </h2>
        </div>

        {}
        <div className="relative">
          {}
          <Button variant="ghost" size="icon" onClick={handlePrev} aria-label="Previous testimonial" className="hidden md:flex absolute -left-2 lg:-left-8 top-1/2 -translate-y-1/2 z-10 rounded-lg w-10 h-10 hover:bg-accent">
            <ChevronLeft className="w-6 h-6" strokeWidth={1} />
          </Button>

          <Button variant="ghost" size="icon" onClick={handleNext} aria-label="Next testimonial" className="hidden md:flex absolute -right-2 lg:-right-8 top-1/2 -translate-y-1/2 z-10 rounded-lg w-10 h-10 hover:bg-accent">
            <ChevronRight className="w-6 h-6" strokeWidth={1} />
          </Button>

          {}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center px-4 md:px-12">
            {}
            <div className="lg:col-span-4">
              <div className="relative aspect-[4/5] overflow-hidden rounded-lg">
                <img src={active.image} data-wvc-srcset={active.srcset} data-wvc-sizes={active.sizes} alt={active.name} loading="lazy" decoding="async" className="w-full h-full object-cover" style={{
                filter: "sepia(0.15) contrast(1.05)"
              }} />
              </div>
              <div className="mt-6 text-center lg:text-left">
                <p className="font-mono text-xs tracking-[0.25em] uppercase text-foreground">
                  {active.name}
                </p>
                <p className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground mt-2">
                  {active.credentials}
                </p>
              </div>
            </div>

            {}
            <div className="lg:col-span-8 lg:pl-4">
              <div className="font-default leading-none mb-2 select-none" style={{
              color: "var(--primary)",
              fontSize: "96px",
              lineHeight: "0.8"
            }}>
                &ldquo;
              </div>
              <blockquote className="font-default italic text-2xl md:text-3xl lg:text-[32px] leading-relaxed text-foreground/95 mb-10">
                {active.quote}
              </blockquote>

              {}
              <div className="flex items-center gap-2 mb-5">
                {Array.from({
                length: active.rating
              }).map((_, i) => <Leaf key={i} data-index={i} className="w-4 h-4" style={{
                color: "var(--primary)",
                fill: "var(--primary)"
              }} strokeWidth={1} />)}
              </div>

              <p className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">
                {active.publication}
              </p>
            </div>
          </div>

          {}
          <div className="flex md:hidden justify-center gap-4 mt-10">
            <Button variant="ghost" size="icon" onClick={handlePrev} aria-label="Previous testimonial" className="rounded-lg w-10 h-10 hover:bg-accent">
              <ChevronLeft className="w-6 h-6" strokeWidth={1} />
            </Button>
            <Button variant="ghost" size="icon" onClick={handleNext} aria-label="Next testimonial" className="rounded-lg w-10 h-10 hover:bg-accent">
              <ChevronRight className="w-6 h-6" strokeWidth={1} />
            </Button>
          </div>

          {}
          <div className="flex items-center justify-center gap-6 mt-14 md:mt-20">
            {thumbnails.map((t, i) => {
            const originalIndex = testimonials.findIndex(item => item.id === t.id);
            return <Button key={t.id} data-index={i} onClick={() => setActiveIndex(originalIndex)} aria-label={`View testimonial from ${t.name}`} variant="ghost" className="group relative transition-all duration-300 p-0 h-auto w-auto rounded-lg">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg overflow-hidden ring-1 ring-border group-hover:ring-2 transition-all duration-300">
                    <img src={t.image} data-wvc-srcset={t.srcset} data-wvc-sizes={t.sizes} alt={t.name} loading="lazy" decoding="async" className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity duration-300" style={{
                  filter: "sepia(0.15) contrast(1.05)"
                }} />
                  </div>
                </Button>;
          })}

            {}
            <Button onClick={() => {}} aria-label={`Current: ${active.name}`} variant="ghost" className="relative order-first p-0 h-auto w-auto rounded-lg">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg overflow-hidden ring-2 transition-all duration-300" style={{
              boxShadow: "0 0 0 2px var(--primary)"
            }}>
                <img src={active.image} data-wvc-srcset={active.srcset} data-wvc-sizes={active.sizes} alt={active.name} loading="lazy" decoding="async" className="w-full h-full object-cover" style={{
                filter: "sepia(0.15) contrast(1.05)"
              }} />
              </div>
            </Button>
          </div>
        </div>

        {}
        <div className="flex items-center justify-center gap-4 mt-20">
          <div className="h-px flex-1 max-w-xs bg-border" />
          <Leaf className="w-5 h-5" style={{
          color: "var(--primary)"
        }} strokeWidth={1} />
          <div className="h-px flex-1 max-w-xs bg-border" />
        </div>
      </div>
    </section>;
}